import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceArea,
} from 'recharts';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronDown, ChevronUp, AlertTriangle, Loader2 } from 'lucide-react';
import { useTrendData } from '../hooks/useTrendData';
import { formatBps } from '../utils/market';

interface MacroTrendPopoverProps {
  indicatorId: string;
  label: string;
  unit: string;
  color: string;
  isOpen: boolean;
  onClose: () => void;
  triggerRect: DOMRect | null;
  prefix?: string;
  divisor?: number;
  decimals?: number;
  currentValue?: number;
  frequency?: string;
  yieldSpread10Y3M?: number;
}

export function MacroTrendPopover({
  indicatorId,
  label,
  unit,
  color,
  isOpen,
  onClose,
  triggerRect,
  prefix = '',
  divisor = 1,
  decimals = 2,
  currentValue,
  frequency,
  yieldSpread10Y3M
}: MacroTrendPopoverProps) {
  const isInflationRelated = indicatorId.toLowerCase().includes('inflation') || 
                             indicatorId.toLowerCase().includes('cpi') || 
                             indicatorId.toLowerCase().includes('pce');
  const isDeepInversion = yieldSpread10Y3M !== undefined && yieldSpread10Y3M <= -1.0;
  const showInversionMarking = isInflationRelated && isDeepInversion;

  const [period, setPeriod] = useState<number>(3);
  const popoverRef = useRef<HTMLDivElement>(null);

  // ✅ Real data via useTrendData hook
  const { data: rawData, loading, isMock, error, refetch } = useTrendData(isOpen ? indicatorId : null, currentValue) as any;

  // Slice data and merge with live currentValue
  const data = useMemo(() => {
    if (!rawData) return [];
    
    // API provides 52 points per year (weekly)
    const pointsNeeded = period * 52;
    let baseData = rawData.slice(-pointsNeeded);

    // Merge with live currentValue from dashboard if it's potentially newer
    if (currentValue !== undefined && baseData.length > 0) {
      const lastPoint = baseData[baseData.length - 1];
      const todayStr = new Date().toISOString().split('T')[0];
      
      // If the last point in history is NOT today, or the value is different, 
      // append/update with the live value to ensure consistency with the card.
      if (lastPoint.date !== todayStr || Math.abs(lastPoint.value - currentValue) > 0.001) {
        // If it's the same date, just update the value
        if (lastPoint.date === todayStr) {
          baseData = [...baseData.slice(0, -1), { ...lastPoint, value: currentValue }];
        } else {
          // If it's a new date, append it
          baseData = [...baseData, { date: todayStr, value: currentValue }];
        }
      }
    }
    
    return baseData;
  }, [rawData, period, currentValue]);

  // Format a raw value using the same rules as the grid
  const formatVal = (val: number) => {
    const divided = val / divisor;
    
    // Use formatCompactUnit for Futures Net Buy
    if (indicatorId === 'krFuturesNetBuy' || unit === 'K' || unit === 'k') {
      const abs = Math.abs(divided);
      const sign = divided < 0 ? '-' : '';
      if (abs >= 1000000) {
        const num = abs / 1000000;
        return `${prefix}${sign}${num % 1 === 0 ? num.toFixed(0) : num.toFixed(1)}M`;
      }
      if (abs >= 1000) {
        const num = abs / 1000;
        return `${prefix}${sign}${num % 1 === 0 ? num.toFixed(0) : num.toFixed(1)}K`;
      }
      return `${prefix}${sign}${abs.toLocaleString()}`;
    }
    if (unit === 'bp') return formatBps(divided);
    if (unit === '계약') return `${prefix}${Math.round(divided).toLocaleString()}${unit}`;
    
    const formatted = divided.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    if (unit === '%') return `${formatted}${unit}`;
    return `${prefix}${formatted}${unit}`;
  };

  const stats = useMemo(() => {
    if (!data || data.length === 0) {
      return { 
        min: currentValue || 0, 
        max: currentValue || 0, 
        avg: currentValue || 0, 
        current: currentValue || 0 
      };
    }
    const values = data.map((d: any) => d.value);
    const sum = values.reduce((acc: number, v: number) => acc + v, 0);
    return {
      min: Math.min(...values),
      max: Math.max(...values),
      avg: values.length > 0 ? sum / values.length : 0,
      current: values[values.length - 1],
    };
  }, [data, currentValue]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !triggerRect) return null;

  const POPOVER_WIDTH = 320;
  const MARGIN = 16;
  
  // Calculate position
  let left = triggerRect.left + (triggerRect.width / 2) - (POPOVER_WIDTH / 2);
  let top = triggerRect.bottom + 12;

  // Screen bounds collision detection
  if (left < MARGIN) left = MARGIN;
  if (left + POPOVER_WIDTH > window.innerWidth - MARGIN) {
    left = window.innerWidth - POPOVER_WIDTH - MARGIN;
  }

  // If popover goes below screen, show it above the trigger
  const estimatedHeight = 300;
  if (top + estimatedHeight > window.innerHeight - MARGIN) {
    top = triggerRect.top - estimatedHeight - 12;
  }

  return (
    <AnimatePresence>
      <motion.div
        ref={popoverRef}
        initial={{ opacity: 0, y: top > triggerRect.top ? -10 : 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: top > triggerRect.top ? -10 : 10, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        className="fixed z-[100] bg-[#1a1f2e]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4"
        style={{
          width: POPOVER_WIDTH,
          left,
          top,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-white/90">{label}</h3>
            {stats.current !== undefined && (
              <div className="flex items-center gap-1.5 bg-white/5 px-2 py-0.5 rounded text-xs font-mono">
                <span style={{ color }} className="font-bold">
                  {formatVal(stats.current)}
                </span>
                <span className="text-[10px] text-white/20">({period}Y Range)</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-white/40 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mock data warning banner */}
        {isMock && !loading && !error && (
          <div className="flex items-center gap-2 px-2 py-1.5 mb-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px]">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span>실제 데이터 연결 실패 — 추정치 표시 중</span>
          </div>
        )}

        {/* Error state */}
        {error ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-500/10 mb-1">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-[12px] font-bold text-white/80">{error}</div>
            <div className="text-[10px] text-white/40 text-center px-4 mb-2">
              일시적인 네트워크 지연이나 서버 문제일 수 있습니다.
            </div>
            <button
              onClick={refetch}
              className="px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-[11px] font-medium transition-colors"
            >
              재시도
            </button>
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            <span className="text-[11px] text-white/30">데이터 로드 중…</span>
          </div>
        ) : (
          <>
            {/* Period Selector */}
            <div className="flex gap-1 p-1 bg-white/5 rounded-xl mb-4 text-[10px]">
              {[1, 3, 5].map((y) => (
                <button
                  key={y}
                  onClick={() => setPeriod(y)}
                  className={`flex-1 py-1 rounded-lg transition-all font-bold ${
                    period === y
                      ? 'bg-white/10 text-white shadow-sm'
                      : 'text-white/40 hover:text-white/60'
                  }`}
                >
                  {y}Y
                </button>
              ))}
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2 text-center overflow-hidden">
                <div className="text-[9px] text-white/30 uppercase flex items-center justify-center gap-1 mb-0.5">
                  <ChevronUp className="w-3 h-3 text-red-400" /> High
                </div>
                <div className="text-xs font-bold truncate">{formatVal(stats.max)}</div>
              </div>
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2 text-center overflow-hidden">
                <div className="text-[9px] text-white/30 uppercase flex items-center justify-center gap-1 mb-0.5 whitespace-nowrap">
                  Avg
                </div>
                <div className="text-xs font-bold text-white/60 truncate">{formatVal(stats.avg)}</div>
              </div>
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2 text-center overflow-hidden">
                <div className="text-[9px] text-white/30 uppercase flex items-center justify-center gap-1 mb-0.5">
                  <ChevronDown className="w-3 h-3 text-blue-400" /> Low
                </div>
                <div className="text-xs font-bold truncate">{formatVal(stats.min)}</div>
              </div>
            </div>

            {/* Chart */}
            <div className="h-[140px] w-full -mx-2 bg-white/[0.01] rounded-xl overflow-hidden relative min-w-0">
              <ResponsiveContainer width="100%" height={140} minHeight={0} minWidth={0} debounce={50}>
                <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`colorTrend_${indicatorId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.03)"
                    vertical={false}
                  />
                  <XAxis dataKey="date" hide />
                  <YAxis hide domain={['auto', 'auto']} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-black/80 border border-white/10 backdrop-blur-md px-2 py-1 rounded-lg text-[10px] shadow-xl">
                            <div className="text-white/40">{payload[0].payload.date}</div>
                            <div className="font-bold" style={{ color }}>
                              {formatVal(payload[0].value as number)}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {showInversionMarking && (
                    <ReferenceArea 
                      y1={-Infinity} 
                      y2={Infinity} 
                      fill="rgba(239, 68, 68, 0.08)" 
                      stroke="none"
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill={`url(#colorTrend_${indicatorId})`}
                    animationDuration={800}
                  />
                </AreaChart>
              </ResponsiveContainer>
              {showInversionMarking && (
                <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-red-500/20 border border-red-500/30 rounded text-[8px] text-red-400 font-bold backdrop-blur-sm z-10 animate-pulse">
                  DEEP INVERSION (10Y-3M &lt; -1.0)
                </div>
              )}
            </div>

            {/* Data source tag */}
            <div className="mt-2 text-[9px] text-white/20 text-right">
              {isMock ? '📊 Estimated data' : `📡 Live data · ${(() => {
                if (indicatorId === 'krCustomerDeposit' || indicatorId === 'krMarginBalance') {
                  return '금융투자협회 종합통계포털';
                }
                if (indicatorId === 'krFuturesNetBuy') {
                  return 'KRX 정보데이터시스템';
                }
                if (indicatorId === 'krCds') {
                  return '국제금융시장 (마켓데이터)';
                }
                const ecosIndicators = [
                  'bokRate', 'krM2', 'krCpi', 'krExports', 'krCreditSpread', 'krYieldSpread',
                  'krCpRate', 'krBsi', 'krExportPrice', 'krImportPrice', 'krPpi'
                ];
                if (ecosIndicators.includes(indicatorId)) {
                  return '한국은행 ECOS';
                }
                return 'FRED / Yahoo Finance';
              })()}`}
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
